"use strict";
/* eslint-disable functional/no-classes */
Object.defineProperty(exports, "__esModule", { value: true });
exports.LocalDate = void 0;
const LocalDateInterval_1 = require("./LocalDateInterval");
const YearQuarter_1 = require("./YearQuarter");
/**
 * A class that represents a date without a time zone.
 * Inspired by Joda Time's LocalDate class.
 */
class LocalDate {
    year;
    month;
    day;
    /**
     * Returns true if the two dates are equal
     *
     * @param one First date
     * @param two Second date
     * @returns True if the two dates are equal
     */
    static isEqual(one, two) {
        return (one.year === two.year && one.month === two.month && one.day === two.day);
    }
    /**
     * Returns true if the first date is before the second date
     *
     * @param one First date
     * @param two Second date
     * @returns True if the first date is before the second date
     */
    static isBefore(one, two) {
        if (one.year < two.year) {
            return true;
        }
        if (one.year > two.year) {
            return false;
        }
        if (one.month < two.month) {
            return true;
        }
        if (one.month > two.month) {
            return false;
        }
        return one.day < two.day;
    }
    /**
     * Returns true if the first date is after the second date
     *
     * @param one First date
     * @param two Second date
     * @returns True if the first date is after the second date
     */
    static isAfter(one, two) {
        if (one.year > two.year) {
            return true;
        }
        if (one.year < two.year) {
            return false;
        }
        if (one.month > two.month) {
            return true;
        }
        if (one.month < two.month) {
            return false;
        }
        return one.day > two.day;
    }
    //
    // CONSTRUCTORS
    //
    /**
     * Creates a new LocalDate instance from a Date instance using the local time zone.
     */
    static fromDateLocal(date) {
        return new LocalDate(date.getFullYear(), date.getMonth() + 1, date.getDate());
    }
    /**
     * Creates a new LocalDate instance from a Date instance using UTC time zone.
     */
    static fromDateUTC(date) {
        return new LocalDate(date.getUTCFullYear(), date.getUTCMonth() + 1, date.getUTCDate());
    }
    /**
     * Create a new LocalDate instance.
     *
     * @param year year (e.g. 2020)
     * @param month month (1-12)
     * @param day day (1-31)
     */
    constructor(year, month, day) {
        this.year = year;
        this.month = month;
        this.day = day;
    }
    //
    // TRANSFORMATIONS
    //
    /**
     * Returns the string representation of the date in the format "YYYY-MM-DD"
     */
    toString() {
        return `${this.year}-${String(this.month).padStart(2, "0")}-${String(this.day).padStart(2, "0")}`;
    }
    /**
     * Convert the LocalDate instance to a Date instance using the local time zone.
     */
    toDateLocal() {
        return new Date(this.year, this.month - 1, this.day);
    }
    /**
     * Convert the LocalDate instance to a Date instance using the UTC time zone.
     */
    toDateUTC() {
        return new Date(Date.UTC(this.year, this.month - 1, this.day));
    }
    /**
     * Returns the Duration from this date to the given date.
     */
    until(end) {
        return new LocalDateInterval_1.LocalDateInterval(this, end).toDuration();
    }
    /**
     * Returns the quarter of the year for this date
     *
     * @returns Quarter of the year
     */
    yearQuarter() {
        return YearQuarter_1.YearQuarter.fromLocalDate(this);
    }
    //
    // COMPARISONS
    //
    /**
     * Returns true if this date is equal to the given date
     *
     * @param date Date to compare
     * @returns True if this date is equal to the given date
     */
    isEqual(date) {
        return LocalDate.isEqual(this, date);
    }
    /**
     * Returns true if this date is before the given date
     *
     * @param date Date to compare
     * @returns True if this date is before the given date
     */
    isBefore(date) {
        return LocalDate.isBefore(this, date);
    }
    /**
     * Returns true if this date is after the given date
     *
     * @param date Date to compare
     * @returns True if this date is after the given date
     */
    isAfter(date) {
        return LocalDate.isAfter(this, date);
    }
    /**
     * Compares this date to the given date.
     *
     * @param date Date to compare
     * @returns 0 if the dates are equal, -1 if this date is before the given date, 1 if this date is after the given date
     */
    compare(date) {
        if (this.isEqual(date)) {
            return 0;
        }
        if (this.isBefore(date)) {
            return -1;
        }
        return 1;
    }
    //
    // OPERATIONS
    //
    /**
     * Returns the date after the given number of days
     *
     * @param days Number of days
     * @returns Date after the given number of days
     */
    plusDays(days) {
        const date = new Date(this.toDateUTC().getTime());
        // eslint-disable-next-line functional/no-expression-statements
        date.setUTCDate(date.getUTCDate() + days);
        return LocalDate.fromDateUTC(date);
    }
    /**
     * Returns the date after the given number of months
     *
     * @param months Number of months
     * @returns Date after the given number of months
     */
    plusMonths(months) {
        const date = new Date(this.toDateUTC().getTime());
        // eslint-disable-next-line functional/no-expression-statements
        date.setUTCMonth(date.getUTCMonth() + months);
        return LocalDate.fromDateUTC(date);
    }
    /**
     * Returns the date after the given number of years
     *
     * @param years Number of years
     * @returns Date after the given number of years
     */
    plusYears(years) {
        return new LocalDate(this.year + years, this.month, this.day);
    }
    /**
     * Returns the date after the provided Duration
     */
    plus(duration) {
        return LocalDate.fromDateUTC(new Date(this.toDateUTC().getTime() + duration.milliseconds));
    }
}
exports.LocalDate = LocalDate;
